import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import theme, { mobileBreakpoint } from '../theme';
import config from '../config';
import { useAuth } from '../AuthContext';
import { ManageButton } from './shared/CommonElements';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  will-change: opacity;
`;

const PopupContainer = styled(motion.div)`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
  border-radius: ${props => props.theme.borderRadius.large};
  padding: 40px;
  max-width: 800px;
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(205, 255, 155, 0.3);
  position: relative;
  will-change: opacity, transform;
  border: 2px solid ${props => props.theme.colors.accent1};
  
  @media (max-width: ${mobileBreakpoint}px) {
    padding: 24px;
    max-height: 90vh;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, ${props => props.theme.colors.secondary} 0%, #e8316d 100%);
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(219, 39, 98, 0.3);

  &:hover {
    background: linear-gradient(135deg, #e8316d 0%, ${props => props.theme.colors.secondary} 100%);
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(219, 39, 98, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const PopupTitle = styled.h2`
  font-size: 28px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.secondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 24px;
  text-align: center;
  font-weight: 700;
  
  @media (max-width: ${mobileBreakpoint}px) {
    font-size: 24px;
    margin-bottom: 20px;
  }
`;

const UserName = styled.div`
  font-size: 18px;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 24px;
  text-align: center;
  font-weight: 600;
`;

const PackageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const PackageItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: rgba(188, 231, 253, 0.13);
  border: 1px solid #bce7fd;
  border-radius: ${props => props.theme.borderRadius.medium};
  
  @media (max-width: ${mobileBreakpoint}px) {
    padding: 12px;
  }
`;

const PackageName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 4px;
`;

const PackageInfo = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
`;

const PriceInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const PriceLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.colors.primary};
  min-width: 120px;
`;

const PriceInput = styled.input`
  flex: 1;
  min-width: 150px;
  padding: 10px 14px;
  border: 2px solid ${props => props.theme.colors.accent1};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 16px;
  outline: none;
  transition: all 0.2s;
  
  &:focus {
    border-color: ${props => props.theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(219, 39, 98, 0.1);
  }
  
  &::placeholder {
    color: #999;
  }
`;

const StandardPrice = styled.div`
  font-size: 12px;
  color: #999;
  font-style: italic;
  margin-top: 4px;
  font-weight: 500;
`;

const PriceError = styled.div`
  font-size: 12px;
  color: #c62828;
  margin-top: 4px;
  font-weight: 500;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
  flex-wrap: wrap;
  
  @media (max-width: ${mobileBreakpoint}px) {
    flex-direction: column;
    justify-content: stretch;
  }
`;

const SaveButton = styled(ManageButton)`
  background: linear-gradient(135deg, ${props => props.theme.colors.secondary} 0%, #e8316d 100%);
  border: 2px solid ${props => props.theme.colors.secondary};
  color: white;
  box-shadow: none;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #e8316d 0%, ${props => props.theme.colors.secondary} 100%);
    box-shadow: 0 6px 20px rgba(219, 39, 98, 0.4);
    border: 2px solid ${props => props.theme.colors.secondary};
    color: white;
    transform: unset;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
  }
`;

const CancelButton = styled(ManageButton)`
  background: white;
  border: 2px solid ${theme.colors.accent1};
  color: black;
  box-shadow: none;
  
  &:hover {
    background: ${theme.colors.accent1};
    color: white;
    box-shadow: none;
  }
  
  @media (max-width: ${mobileBreakpoint}px) {
    width: 100%;
  }
`;

const shimmer = `
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }
`;

const SkeletonBox = styled.div`
  ${shimmer}
  background: linear-gradient(
    90deg,
    rgba(188, 231, 253, 0.1) 0%,
    rgba(188, 231, 253, 0.2) 50%,
    rgba(188, 231, 253, 0.1) 100%
  );
  background-size: 1000px 100%;
  animation: shimmer 2s infinite;
  border-radius: ${props => props.theme.borderRadius.medium};
`;

const SkeletonItem = styled(SkeletonBox)`
  padding: 16px;
  background: rgba(188, 231, 253, 0.13);
  border: 1px solid #bce7fd;
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  @media (max-width: ${mobileBreakpoint}px) {
    padding: 12px;
  }
`;

const SkeletonLine = styled(SkeletonBox)`
  height: ${props => props.height || '20px'};
  width: ${props => props.width || '100%'};
  border-radius: 4px;
`;

const SkeletonInput = styled(SkeletonBox)`
  height: 42px;
  flex: 1;
  min-width: 150px;
  border-radius: ${props => props.theme.borderRadius.medium};
`;

const SkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const SkeletonInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ffcdd2;
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: 12px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const SuccessMessage = styled.div`
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #c8e6c9;
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: 12px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const UserPackagePricesPopup = ({ isOpen, onClose, username, userDisplayName, onPricesSaved }) => {
  const { token } = useAuth();
  const [packages, setPackages] = useState([]);
  const [packagePrices, setPackagePrices] = useState({});
  const [priceErrors, setPriceErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!isOpen || !username) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setSuccess(null);

      try {
        // Fetch all packages
        const packagesResponse = await fetch(config.getApiUrl('/api/allusers/all-packages'), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!packagesResponse.ok) {
          throw new Error('Fehler beim Laden der Pakete');
        }

        const packagesData = await packagesResponse.json();
        // Filter out social discount packages
        const filteredPackages = packagesData.filter(pkg => !pkg.isSocialDiscount);
        setPackages(filteredPackages);

        // Fetch existing user-specific prices
        const pricesResponse = await fetch(config.getApiUrl(`/api/allusers/user-package-prices/${username}`), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (pricesResponse.ok) {
          const pricesData = await pricesResponse.json();
          setPackagePrices(pricesData.packagePrices || {});
        }
      } catch (err) {
        setError(err.message || 'Fehler beim Laden der Daten');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, username, token]);

  const validatePrice = (price, packageData) => {
    if (!price || price.trim() === '') {
      return { valid: true, error: null }; // Empty is valid (will use standard price)
    }
    
    // Remove "€" and spaces for validation
    const cleanedPrice = price.replace(/€/g, '').trim();
    
    // Check if it's a valid number format (allows comma as decimal separator)
    // Pattern: optional digits, optional comma, optional digits
    const pricePattern = /^(\d+([,]\d{0,2})?)$/;
    
    if (!pricePattern.test(cleanedPrice)) {
      return { 
        valid: false, 
        error: 'Bitte gib einen gültigen Preis ein (z.B. 145,00 oder 145,50)' 
      };
    }
    
    // Check if it's a positive number
    const numericValue = parseFloat(cleanedPrice.replace(',', '.'));
    if (isNaN(numericValue) || numericValue <= 0) {
      return { 
        valid: false, 
        error: 'Der Preis muss eine positive Zahl sein' 
      };
    }
    
    // Validate that price is divisible by number of lessons in the package
    if (packageData && packageData.budgets) {
      // Calculate total number of lessons
      const totalLessons = Object.values(packageData.budgets).reduce((sum, count) => sum + (parseInt(count) || 0), 0);
      
      if (totalLessons > 0) {
        // Convert to cents to avoid floating point precision issues
        const priceInCents = Math.round(numericValue * 100);
        const remainder = priceInCents % totalLessons;
        
        if (remainder !== 0) {
          return {
            valid: false,
            error: `Der Preis ist nicht durch die Anzahl der Lektionen (${totalLessons}) teilbar.`
          };
        }
      }
    }
    
    return { valid: true, error: null };
  };

  const handlePriceChange = (packageId, value) => {
    const packageData = packages.find(pkg => pkg.id === packageId);
    const validation = validatePrice(value, packageData);
    
    setPackagePrices(prev => ({
      ...prev,
      [packageId]: value
    }));
    
    setPriceErrors(prev => ({
      ...prev,
      [packageId]: validation.error
    }));
    
    setSuccess(null);
    setError(null);
  };

  const handleSave = async () => {
    // Validate all prices before saving
    const validationErrors = {};
    let hasErrors = false;
    
    for (const [packageId, price] of Object.entries(packagePrices)) {
      const packageData = packages.find(pkg => pkg.id === packageId);
      const validation = validatePrice(price, packageData);
      if (!validation.valid) {
        validationErrors[packageId] = validation.error;
        hasErrors = true;
      }
    }
    
    if (hasErrors) {
      setPriceErrors(validationErrors);
      setError('Bitte korrigiere die Fehler in den Preiseingaben');
      return;
    }
    
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(config.getApiUrl(`/api/allusers/user-package-prices/${username}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(packagePrices)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Fehler beim Speichern der Preise');
      }

      if (onPricesSaved) {
        onPricesSaved();
      }
      // Close immediately after showing success message briefly
      setTimeout(() => {
        onClose();
      }, 0);
    } catch (err) {
      setError(err.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          key="user-package-prices-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onClick={onClose}
        >
          <PopupContainer
            key="user-package-prices-container"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
          <CloseButton onClick={onClose}>×</CloseButton>
          
          <PopupTitle>Preise anpassen</PopupTitle>
          
          {userDisplayName && (
            <UserName>Benutzer: {userDisplayName}</UserName>
          )}

          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          {loading ? (
            <SkeletonContainer>
              {[1, 2, 3].map((i) => (
                <SkeletonItem key={i}>
                  <SkeletonLine height="20px" width="60%" />
                  <SkeletonLine height="16px" width="40%" />
                  <SkeletonInputRow>
                    <SkeletonLine height="16px" width="120px" />
                    <SkeletonInput />
                  </SkeletonInputRow>
                </SkeletonItem>
              ))}
            </SkeletonContainer>
          ) : (
            <>
              <PackageList>
                {packages.map((pkg) => (
                  <PackageItem key={pkg.id}>
                    <PackageName>{pkg.name}</PackageName>
                    <PackageInfo>
                      Standardpreis: {pkg.price}
                    </PackageInfo>
                    <PriceInputContainer>
                      <PriceLabel htmlFor={`price-${pkg.id}`}>
                        Individueller Preis:
                      </PriceLabel>
                      <PriceInput
                        id={`price-${pkg.id}`}
                        type="text"
                        placeholder={pkg.price}
                        value={packagePrices[pkg.id] || ''}
                        onChange={(e) => handlePriceChange(pkg.id, e.target.value)}
                        style={{
                          borderColor: priceErrors[pkg.id] ? '#c62828' : undefined
                        }}
                      />
                    </PriceInputContainer>
                    {priceErrors[pkg.id] && (
                      <PriceError>{priceErrors[pkg.id]}</PriceError>
                    )}
                    {packagePrices[pkg.id] && !priceErrors[pkg.id] && (
                      <StandardPrice>
                        Leer lassen, um Standardpreis zu verwenden
                      </StandardPrice>
                    )}
                  </PackageItem>
                ))}
              </PackageList>

              <ButtonContainer>
                <CancelButton onClick={onClose} disabled={saving}>
                  Abbrechen
                </CancelButton>
                <SaveButton 
                  onClick={handleSave} 
                  disabled={saving || Object.keys(priceErrors).some(key => priceErrors[key] !== null)}
                >
                  {saving ? 'Speichere...' : 'Speichern'}
                </SaveButton>
              </ButtonContainer>
            </>
          )}
          </PopupContainer>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default UserPackagePricesPopup;

